import { LibraryDB } from './LibraryDB';

const BASE_URL = 'https://gutendex.com/books/';

export interface GutendexBook {
    id: number;
    title: string;
    authors: { name: string; birth_year?: number; death_year?: number }[];
    translators: { name: string; birth_year?: number; death_year?: number }[];
    subjects: string[];
    bookshelves: string[];
    languages: string[];
    copyright: boolean | null;
    media_type: string;
    formats: Record<string, string>;
    download_count: number;
}

export interface GutendexResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: GutendexBook[];
}

export const GutendexService = {
    // In-Memory Cache (Simple LRU-ish, cleared on flush)
    _cache: new Map<string, { data: GutendexResponse, ts: number }>(),

    async search(query: string, lang: string = 'en', page: number = 1, sort?: string, topic?: string): Promise<GutendexResponse> {
        // Cache Key
        const cacheKey = `gutendex_q=${query}_l=${lang}_p=${page}_s=${sort || 'def'}_t=${topic || 'none'}`;

        // 1. Check In-Memory (1h)
        const mem = this._cache.get(cacheKey);
        if (mem && (Date.now() - mem.ts < 3600000)) {
            return mem.data;
        }

        // 2. Check LocalStorage (6h)
        try {
            const lsItem = localStorage.getItem(cacheKey);
            if (lsItem) {
                const parsed = JSON.parse(lsItem);
                if (Date.now() - parsed.ts < 21600000) { // 6 hours
                    // Populate memory for faster next access
                    this._cache.set(cacheKey, parsed);
                    return parsed.data;
                }
            }
        } catch (e) { /* Ignore LS errors */ }

        // 3. Network Fetch
        const url = new URL(BASE_URL);
        if (query) url.searchParams.set('search', query);
        if (topic) url.searchParams.set('topic', topic);

        if (lang !== 'all') {
            url.searchParams.set('languages', lang);
        }
        if (page > 1) {
            url.searchParams.set('page', page.toString());
        }
        if (sort) {
            url.searchParams.set('sort', sort);
        }

        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`Gutendex API error: ${res.statusText}`);

        const data: GutendexResponse = await res.json();

        // 4. Update Caches
        const cacheObj = { data, ts: Date.now() };
        this._cache.set(cacheKey, cacheObj);

        try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheObj));
        } catch (e) {
            // LS Full, try clearing old items? For now just skip.
        }

        // Legacy DB Cache (Keep for potential offline use of simple queries)
        if (!sort && !topic) {
            await LibraryDB.setCache(query, lang, page, data);
        }

        return data;
    },

    // Helper to pick best format candidates
    getDownloadCandidates(book: GutendexBook): { mime: string, url: string }[] {
        const candidates: { mime: string, url: string }[] = [];
        const formats = book.formats;

        // Priority 1: Plain Text (most reliable)
        if (formats['text/plain; charset=utf-8']) {
            candidates.push({ mime: 'text/plain; charset=utf-8', url: formats['text/plain; charset=utf-8'] });
        }
        if (formats['text/plain']) {
            candidates.push({ mime: 'text/plain', url: formats['text/plain'] });
        }
        if (formats['text/plain; charset=us-ascii']) {
            candidates.push({ mime: 'text/plain; charset=us-ascii', url: formats['text/plain; charset=us-ascii'] });
        }

        // Priority 2: HTML (good structure)
        if (formats['text/html']) {
            candidates.push({ mime: 'text/html', url: formats['text/html'] });
        }

        // Priority 3: EPUB (Offline first logic will sort this to top if preferEpub is true)
        if (formats['application/epub+zip']) {
            candidates.push({ mime: 'application/epub+zip', url: formats['application/epub+zip'] });
        }

        // Priority 4: Hardcoded Fallbacks (Only if no valid formats found above, OR if they all fail - caller iterates)
        // We add them here so the caller has options if the primary links 404.
        const id = book.id;
        // https://www.gutenberg.org/files/{id}/{id}-0.txt
        candidates.push({ mime: 'fallback/files-0-txt', url: `https://www.gutenberg.org/files/${id}/${id}-0.txt` });
        // https://www.gutenberg.org/cache/epub/{id}/pg{id}.txt
        candidates.push({ mime: 'fallback/cache-txt', url: `https://www.gutenberg.org/cache/epub/${id}/pg${id}.txt` });

        return candidates;
    }
};
